import React, { useEffect, useMemo, useState } from "react";
import { AutoComplete, Input, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import useDebounce from "Explore/Components/UseDebounce/useDebounce";
import styles from "./BenchmarkEntityPicker.module.scss";

/**
 * Multi-select entity picker built on top of the existing
 * `getSuggestSearch*` services. Surfaces an ant-design AutoComplete for
 * searching by name, and renders selected ids as chips below.
 *
 * Props:
 * - `selectedIds`            string[]
 * - `labelMap`               Record<string, string> mapping id → display name
 * - `onAdd(id, name, item)`  called when a suggestion is picked
 * - `onRemove(id)`           called when a chip is removed
 * - `functionSearch`         the suggest service (e.g. getSuggestSearchInstitutions). Omit when using `localOptions`.
 * - `localOptions`           array of `{ id, name }` for client-side filtering (no API call)
 * - `valueField`             id field on suggest results (default "id")
 * - `placeholder`            input placeholder text
 * - `emptyHint`              text shown beneath the input when empty
 * - `minSearchLength`        minimum query length before triggering search (default 2)
 */
const BenchmarkEntityPicker = ({
  selectedIds = [],
  labelMap = {},
  onAdd,
  onRemove,
  functionSearch,
  localOptions,
  valueField = "id",
  placeholder = "Search...",
  emptyHint,
  minSearchLength = 2,
}) => {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const debouncedValue = useDebounce(query);

  // Coerce selected ids to strings so the dedupe check matches regardless
  // of whether the suggest API returns numeric or string ids (entity pickers
  // often return numbers; keyword/researcher hashes are already strings).
  const selectedSet = useMemo(
    () => new Set((selectedIds || []).map((id) => String(id))),
    [selectedIds]
  );

  useEffect(() => {
    if (!debouncedValue || debouncedValue.length < minSearchLength) {
      setOptions([]);
      setIsLoading(false);
      return undefined;
    }

    // Client-side filtering when consumer provides a local list (e.g. keywords).
    if (Array.isArray(localOptions)) {
      const lower = debouncedValue.toLowerCase();
      const items = localOptions
        .filter((opt) => (opt?.name || "").toLowerCase().includes(lower))
        .slice(0, 12)
        .map((opt) => {
          const id = opt?.[valueField] ?? opt?.id;
          const name = opt?.name || String(id);
          const label = opt?.institution_name
            ? `${name} (${opt.institution_name})`
            : name;
          return {
            ...opt,
            value: id != null ? String(id) : label,
            label,
            name,
            _entityId: id,
          };
        });
      setOptions(items);
      setIsLoading(false);
      return undefined;
    }

    if (typeof functionSearch !== "function") {
      setOptions([]);
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);
    functionSearch(debouncedValue, (res) => {
      if (cancelled) return;
      const items = (res || []).map((item) => {
        const id = item?.[valueField];
        const name = item?.name || String(id);
        const label = item?.institution_name
          ? `${name} (${item.institution_name})`
          : name;
        return {
          ...item,
          value: id != null ? String(id) : label,
          label,
          name,
          _entityId: id,
        };
      });
      setOptions(items);
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [debouncedValue, functionSearch, localOptions, valueField, minSearchLength]);

  const visibleOptions = useMemo(
    () =>
      options
        .filter(
          (opt) =>
            opt._entityId != null && !selectedSet.has(String(opt._entityId))
        )
        .map((opt) => ({
          value: String(opt._entityId),
          label: opt.label,
          _entityId: opt._entityId,
          _raw: opt,
        })),
    [options, selectedSet]
  );

  const handleSearch = (value) => setQuery(value);

  const handleSelect = (_, option) => {
    if (!option || option._entityId == null) return;
    onAdd?.(option._entityId, option.label, option._raw);
    setQuery("");
    setOptions([]);
  };

  return (
    <div className={styles.picker}>
      <AutoComplete
        value={query}
        options={visibleOptions}
        placeholder={placeholder}
        onChange={handleSearch}
        onSelect={handleSelect}
        style={{ width: "100%" }}
        dropdownRender={(menu) =>
          isLoading ? (
            <div className={styles.loading}>
              <Spin size="small" />
            </div>
          ) : (
            menu
          )
        }
      >
        <Input suffix={<SearchOutlined />} />
      </AutoComplete>

      {selectedIds.length > 0 ? (
        <div className={styles.chips}>
          {selectedIds.map((id) => (
            <span key={id} className={styles.chip}>
              <span className={styles["chip__text"]} title={labelMap[id] || id}>
                {labelMap[id] || id}
              </span>
              <button
                type="button"
                className={styles["chip__remove"]}
                onClick={() => onRemove?.(id)}
                aria-label={`Remove ${labelMap[id] || id}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : emptyHint ? (
        <div className={styles.hint}>{emptyHint}</div>
      ) : null}
    </div>
  );
};

export default BenchmarkEntityPicker;
